import { useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Instagram, MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import EnquiryForm from "../components/EnquiryForm";
import { CONTACT, FAQS } from "../data/site";

export default function Admissions() {
  const [params] = useSearchParams();
  const defaultProgram = params.get("program") || "";
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#faq") {
      const t = setTimeout(() => {
        document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [hash]);

  const waHref = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
    "Hi The Thinkstitute! I'd like to enquire about admissions."
  )}`;

  const quickLinks = [
    { icon: Phone, label: "Call Us", value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phone}`, bg: "#3D348B", testid: "call-link" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat instantly", href: waHref, bg: "#00A896", testid: "whatsapp-link" },
    { icon: Instagram, label: "Instagram", value: CONTACT.instagramHandle, href: CONTACT.instagram, bg: "#FF7A29", testid: "instagram-link" },
  ];

  return (
    <div data-testid="admissions-page" className="pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <Reveal>
          <span className="font-bold text-teal-brand">ADMISSIONS</span>
          <h1 className="font-display font-extrabold text-5xl sm:text-7xl text-navy mt-2 leading-[0.95]">
            Let&apos;s start with a <span className="text-marigold">free chat.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-navy/70">
            Fill in the form and our team will call you within 48 hours to book
            your consultation. No pressure, no obligations.
          </p>
        </Reveal>
      </section>

      {/* Quick contact links */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <div className="grid sm:grid-cols-3 gap-4">
          {quickLinks.map((q, i) => (
            <Reveal key={q.label} delay={i * 0.08}>
              <a
                href={q.href}
                target={q.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={q.testid}
                className="flex items-center gap-4 rounded-2xl bg-white border border-navy/10 p-5 hover:shadow-lg transition-shadow"
              >
                <span className="grid place-items-center w-12 h-12 rounded-xl text-white shrink-0" style={{ backgroundColor: q.bg }}>
                  <q.icon size={22} />
                </span>
                <span>
                  <span className="block font-bold text-navy">{q.label}</span>
                  <span className="block text-sm text-navy/60">{q.value}</span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Form + Map */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid lg:grid-cols-5 gap-8">
          <Reveal className="lg:col-span-3">
            <div className="rounded-3xl bg-white border border-navy/10 p-7 sm:p-10">
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-navy mb-6">
                Enquiry Form
              </h2>
              <EnquiryForm defaultProgram={defaultProgram} />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="rounded-3xl overflow-hidden border border-navy/10 h-full min-h-[400px] flex flex-col">
              <div className="p-6 bg-navy text-cream">
                <div className="flex items-center gap-2 font-bold">
                  <MapPin size={18} className="text-marigold" /> Visit Our Campus
                </div>
                <p className="mt-2 text-sm text-cream/70">{CONTACT.address}</p>
              </div>
              <iframe
                title="The Thinkstitute location"
                src={CONTACT.mapEmbed}
                className="w-full flex-1 min-h-[280px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="pb-28" data-testid="faq">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-12">
            <span className="font-bold text-teal-brand">GOOD TO KNOW</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-2">
              Frequently Asked Questions
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="rounded-2xl bg-white border border-navy/10 px-6"
                >
                  <AccordionTrigger className="font-display font-bold text-lg text-navy text-left hover:no-underline hover:text-marigold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-navy/70 text-base leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
